import React from 'react';
import { Box } from '@chakra-ui/react';

import { getFormatDateFromObject, getDateFromString } from 'shared-components';
import { IRisk } from 'types';

import { SummaryRow } from '../../../Summary/SummaryRow';
import { isFundingRisk } from '../../../../utilities/risks';

interface Props {
  id: string;
  showRisks: boolean;
  risk: IRisk['financeDocsRisk']['educationSponsorship'];
  hasOfficialLetterhead: boolean;
  nameAndLogoVisible: boolean;
  officiallySigned: boolean;
  hasDate: boolean;
  dateOnLetter: string;
  selectedTextBodyReq: string[];
  hasContactDetails: boolean;
  fundConditions: boolean;
  isMoreThan6MonthsOld: boolean;
  uploadedBy?: string;
}

export default function LoanLetter({
  id,
  showRisks,
  risk,
  hasOfficialLetterhead,
  nameAndLogoVisible,
  officiallySigned,
  hasDate,
  dateOnLetter,
  selectedTextBodyReq,
  hasContactDetails,
  fundConditions,
  isMoreThan6MonthsOld,
  uploadedBy,
}: Props) {
  const getBodyTextAnswers = () => {
    const questions = [
      {
        key: 'fullName',
        value: 'Full name matches passport',
      },
      {
        key: 'orgName',
        value: 'Loan provider',
      },
      {
        key: 'courseName',
        value: 'Course name',
      },
      {
        key: 'contributionAmount',
        value: 'Contribution amount',
      },
      {
        key: 'periodCovered',
        value: 'Period covered',
      },
      {
        key: 'loanName',
        value: 'Loan type and regulatory body',
      },
      {
        key: 'fundsReleased',
        value: 'When funds will be released',
      },
    ];

    const answers: React.ReactNode[] = [];
    questions.forEach((question, index) => {
      if (selectedTextBodyReq.includes(question.key)) {
        answers.push(<Box key={`body-text-answer-${index}`}>{question.value}</Box>);
      } else {
        answers.push(
          <Box color={'warning'} key={`body-text-answer-not-selected-${index}`}>
            {question.value}
          </Box>,
        );
      }
    });

    return answers;
  };

  if (
    hasOfficialLetterhead === undefined &&
    nameAndLogoVisible === undefined &&
    officiallySigned === undefined &&
    hasDate === undefined &&
    fundConditions === undefined &&
    isMoreThan6MonthsOld === undefined
  ) {
    return <div>Uploaded by {uploadedBy ? uploadedBy : 'University'}</div>;
  }
  return (
    <>
      <SummaryRow
        key="hasOfficialLetterhead"
        label="Official Letterhead"
        fullLabel="Is your letter printed on official letterhead?"
        value={hasOfficialLetterhead ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasOfficialLetterhead', id)}
      />

      <SummaryRow
        key="nameAndLogoVisible"
        label="Name and Logo Visible"
        fullLabel="Is the loan provider's name and logo visible on the letterhead?"
        value={nameAndLogoVisible ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'nameAndLogoVisible', id)}
      />

      <SummaryRow
        key="officiallySigned"
        label="Officially Signed"
        fullLabel="Is it officially stamped and signed?"
        value={officiallySigned ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'officiallySigned', id)}
      />

      <SummaryRow
        key="hasDate"
        label="Has Date"
        fullLabel="Is there a date on the letter?"
        value={hasDate ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasDate', id)}
      />

      {hasDate && (
        <SummaryRow
          key="dateOnLetter"
          label="Date"
          fullLabel="What is the date on the letter?"
          value={getFormatDateFromObject(getDateFromString(dateOnLetter))}
          isRisk={showRisks && isFundingRisk(risk, 'isMoreThan6MonthsOld', id)}
        />
      )}

      <SummaryRow
        key="selectedTextBodyReq"
        label="Included"
        fullLabel="Does the body text of the letter include:"
        value={selectedTextBodyReq ? getBodyTextAnswers() : 'N/A'}
        isRisk={showRisks && isFundingRisk(risk, 'hasAllNames', id)}
      />

      <SummaryRow
        key="fundConditions"
        label="Any conditions"
        fullLabel="Are there any conditions on your funds? (aside from a successful Student visa application)"
        value={fundConditions ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'fundConditions', id)}
      />

      <SummaryRow
        key="hasContactDetails"
        label="Has Contact Details"
        fullLabel="Are contact details for your loan provider included?"
        value={hasContactDetails ? 'Yes' : 'No'}
        isRisk={showRisks && isFundingRisk(risk, 'hasContactDetails', id)}
      />
    </>
  );
}
